import React from 'react';
import { FORMAT_DATE } from '@/constants/date-formats';
import { salesRoute } from '@/modules/sales/route';
import { createRoute } from '@tanstack/react-router';
import { formatDate } from 'date-fns';
import * as z from 'zod';

const dateSchema = z.string().regex(/^\d{2}-\d{2}-\d{4}$/, 'Date must be in DD-MM-YYYY format');

const storageKey = 'payments-search-params'; // * Key to store search params in session storage, we will use safe constants for this later on
const resetStorageKey = 'payments-reset-flag'; // * Key to determine if we need to reset the search params to default values

const paymentsSearchSchema = z.object({
  page: z.coerce.number().min(1).default(1),
  size: z.coerce.number().min(50).default(50),
  from: dateSchema.default(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return formatDate(date, FORMAT_DATE);
  }), // * Start date for the payments (default: 1 month ago)
  to: dateSchema.default(() => formatDate(new Date(), FORMAT_DATE)), // * End date for the payments (default: today)
  sort: z.enum(['amount', 'date', 'group']).default('date'),
  order: z.enum(['asc', 'desc']).default('desc'), // * Sort order
  hide: z.array(z.string()).optional(), // * Columns to hide <- We can restrict this to specific column ids per route if needed
  group: z.array(z.string()).optional(), // * User groups filter
  txn: z.string().optional(), // * Transaction ID filter
  amount: z.string().optional(), // * Amount filter
  ticket: z.string().optional(), // * Ticket ID filter
  card: z.enum(['debit', 'credit']).optional(), // * Card filter
  regd: z.enum(['true', 'false']).optional(), // * Registered user filter
  user_email: z.string().optional(), // * User email filter
});

export type paymentsSearchSchemaType = z.infer<typeof paymentsSearchSchema>;

export const paymentsRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: 'payments',
  component: React.lazy(async () => await import('@/app/routes/Sales/payments/payment-list')),
  wrapInSuspense: true,
  validateSearch: (search = {}) => {
    const isResetting = sessionStorage.getItem(resetStorageKey);
    const stored = sessionStorage.getItem(storageKey);

    if (stored && !isResetting) {
      return paymentsSearchSchema.parse({ ...JSON.parse(stored), ...search });
    }

    if (isResetting) {
      sessionStorage.removeItem(resetStorageKey);
      sessionStorage.removeItem(storageKey);
    }

    return paymentsSearchSchema.parse(search);
  },
  onError: () => {
    sessionStorage.setItem('payments-had-error', 'true');
  },
  onLeave: (ctx) => {
    // * If the reset flag is present, remove it and skip saving the state
    if (sessionStorage.getItem(resetStorageKey)) {
      return;
    }
    // * If there was an error, remove the flag and skip saving the state
    if (sessionStorage.getItem('payments-had-error')) {
      sessionStorage.removeItem('payments-had-error');
      return;
    }
    sessionStorage.setItem(storageKey, JSON.stringify(ctx.search));
  },
});
